import {
  pluginConnectionState,
  type ForgePlugin,
  type ForgePluginId,
  type PluginConnectionAttempt,
} from "./pluginsPage.logic";

export type PluginConnectionPhase = "loading" | "ready" | "unavailable";

export interface PluginConnectionCapabilities {
  readonly connect: boolean;
  readonly disconnect: boolean;
}

export interface PluginConnection extends ForgePlugin {
  readonly capabilities: PluginConnectionCapabilities;
}

export type PluginConnectionAttempts = Readonly<
  Partial<Record<ForgePluginId, PluginConnectionAttempt>>
>;

export interface PluginConnectionSnapshot {
  readonly phase: PluginConnectionPhase;
  readonly plugins: ReadonlyArray<PluginConnection>;
  readonly attempts: PluginConnectionAttempts;
  readonly failure: string | null;
  readonly revision: number;
}

export type PluginConnectionRequest =
  | { readonly action: "connect"; readonly pluginId: ForgePluginId }
  | { readonly action: "disconnect"; readonly pluginId: ForgePluginId };

export type PluginConnectionRequestOutcome =
  | { readonly kind: "started"; readonly pluginId: ForgePluginId }
  | { readonly kind: "already-active"; readonly pluginId: ForgePluginId }
  | {
      readonly kind: "completed";
      readonly action: "connect" | "disconnect";
      readonly pluginId: ForgePluginId;
    }
  | {
      readonly kind: "not-available";
      readonly action: "connect" | "disconnect";
      readonly pluginId: ForgePluginId;
    }
  | { readonly kind: "failed"; readonly pluginId: ForgePluginId; readonly message: string };

export interface AuthorizationWindow {
  readonly closed: boolean;
  readonly close: () => void;
  readonly navigate: (url: string) => void;
}

export interface PluginConnectionDependencies {
  readonly load: () => Promise<ReadonlyArray<ForgePlugin>>;
  readonly beginAuthorization: (pluginId: ForgePluginId) => Promise<{
    readonly alreadyConnected?: boolean;
    readonly authUrl?: string;
  }>;
  readonly disconnect: (pluginId: ForgePluginId) => Promise<void>;
  readonly openAuthorizationWindow: () => AuthorizationWindow | null;
  readonly schedule: (callback: () => void, delayMs: number) => number;
  readonly cancelSchedule: (scheduleId: number) => void;
  readonly now: () => number;
}

export interface PluginConnections {
  readonly read: () => PluginConnectionSnapshot;
  readonly subscribe: (listener: () => void) => () => void;
  readonly reconcile: () => Promise<void>;
  readonly request: (request: PluginConnectionRequest) => Promise<PluginConnectionRequestOutcome>;
  readonly dispose: () => void;
}

const POLL_INTERVAL_MS = 1_500;
const ATTEMPT_TIMEOUT_MS = 90_000;

export function pluginConnectionCapabilities(plugin: ForgePlugin): PluginConnectionCapabilities {
  return { connect: true, disconnect: plugin.connectionKind === "github" };
}

function normalize(plugins: ReadonlyArray<ForgePlugin>): ReadonlyArray<PluginConnection> {
  return plugins.map((plugin) => ({
    ...plugin,
    capabilities: pluginConnectionCapabilities(plugin),
  }));
}

function failureMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Plugin connection failed";
}

export function createPluginConnections(
  dependencies: PluginConnectionDependencies,
): PluginConnections {
  const listeners = new Set<() => void>();
  const schedules = new Map<ForgePluginId, number>();
  const windows = new Map<ForgePluginId, AuthorizationWindow>();
  let reconciliationGeneration = 0;
  let disposed = false;
  let snapshot: PluginConnectionSnapshot = {
    phase: "loading",
    plugins: [],
    attempts: {},
    failure: null,
    revision: 0,
  };

  const publish = (next: PluginConnectionSnapshot): void => {
    snapshot = next;
    for (const listener of listeners) listener();
  };

  const setAttempt = (pluginId: ForgePluginId, attempt: PluginConnectionAttempt | null): void => {
    const attempts = { ...snapshot.attempts };
    if (attempt === null) delete attempts[pluginId];
    else attempts[pluginId] = attempt;
    publish({ ...snapshot, attempts });
  };

  const clearAttempt = (pluginId: ForgePluginId): void => {
    const scheduleId = schedules.get(pluginId);
    if (scheduleId !== undefined) dependencies.cancelSchedule(scheduleId);
    schedules.delete(pluginId);
    windows.get(pluginId)?.close();
    windows.delete(pluginId);
    setAttempt(pluginId, null);
  };

  const reconcile = async (): Promise<void> => {
    if (disposed) return;
    const generation = ++reconciliationGeneration;
    publish({
      ...snapshot,
      phase: snapshot.revision === 0 ? "loading" : snapshot.phase,
      failure: null,
    });
    try {
      const plugins = normalize(await dependencies.load());
      if (disposed || generation !== reconciliationGeneration) return;
      publish({
        ...snapshot,
        phase: "ready",
        plugins,
        failure: null,
        revision: snapshot.revision + 1,
      });
    } catch (error) {
      if (disposed || generation !== reconciliationGeneration) return;
      publish({
        ...snapshot,
        phase: snapshot.plugins.length === 0 ? "unavailable" : "ready",
        failure: failureMessage(error),
        revision: snapshot.revision + 1,
      });
    }
  };

  const scheduleReconciliation = (pluginId: ForgePluginId, startedAt: number): void => {
    const poll = async (): Promise<void> => {
      await reconcile();
      if (disposed || snapshot.attempts[pluginId] === undefined) return;
      const plugin = snapshot.plugins.find((candidate) => candidate.id === pluginId);
      if (plugin && pluginConnectionState(plugin) === "connected") {
        clearAttempt(pluginId);
        return;
      }
      const authorizationWindow = windows.get(pluginId);
      if (authorizationWindow?.closed || dependencies.now() - startedAt > ATTEMPT_TIMEOUT_MS) {
        clearAttempt(pluginId);
        return;
      }
      schedules.set(
        pluginId,
        dependencies.schedule(() => void poll(), POLL_INTERVAL_MS),
      );
    };
    schedules.set(
      pluginId,
      dependencies.schedule(() => void poll(), POLL_INTERVAL_MS),
    );
  };

  const disconnect = async (pluginId: ForgePluginId): Promise<PluginConnectionRequestOutcome> => {
    try {
      await dependencies.disconnect(pluginId);
      await reconcile();
      return { kind: "completed", action: "disconnect", pluginId };
    } catch (error) {
      return { kind: "failed", pluginId, message: failureMessage(error) };
    }
  };

  const connect = async (pluginId: ForgePluginId): Promise<PluginConnectionRequestOutcome> => {
    if (snapshot.attempts[pluginId]) return { kind: "already-active", pluginId };

    const authorizationWindow = dependencies.openAuthorizationWindow();
    if (authorizationWindow === null) return { kind: "failed", pluginId, message: "Popup blocked" };
    windows.set(pluginId, authorizationWindow);
    const startedAt = dependencies.now();
    setAttempt(pluginId, { pluginId, status: "opening" });
    try {
      const authorization = await dependencies.beginAuthorization(pluginId);
      if (disposed || snapshot.attempts[pluginId] === undefined) {
        authorizationWindow.close();
        return { kind: "failed", pluginId, message: "Plugin connection cancelled" };
      }
      if (authorization.alreadyConnected) {
        await reconcile();
        clearAttempt(pluginId);
        return { kind: "completed", action: "connect", pluginId };
      }
      if (!authorization.authUrl) throw new Error("Authorization URL unavailable");
      authorizationWindow.navigate(authorization.authUrl);
      setAttempt(pluginId, { pluginId, status: "waiting" });
      scheduleReconciliation(pluginId, startedAt);
      return { kind: "started", pluginId };
    } catch (error) {
      clearAttempt(pluginId);
      return { kind: "failed", pluginId, message: failureMessage(error) };
    }
  };

  const request = (input: PluginConnectionRequest): Promise<PluginConnectionRequestOutcome> => {
    if (disposed) {
      return Promise.resolve({
        kind: "failed",
        pluginId: input.pluginId,
        message: "Plugin connections are unavailable",
      });
    }
    const plugin = snapshot.plugins.find((candidate) => candidate.id === input.pluginId);
    if (!plugin?.capabilities[input.action]) {
      return Promise.resolve({
        kind: "not-available",
        action: input.action,
        pluginId: input.pluginId,
      });
    }
    return input.action === "disconnect" ? disconnect(input.pluginId) : connect(input.pluginId);
  };

  return {
    read: () => snapshot,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    reconcile,
    request,
    dispose: () => {
      disposed = true;
      reconciliationGeneration += 1;
      for (const pluginId of Object.keys(snapshot.attempts) as ForgePluginId[]) {
        clearAttempt(pluginId);
      }
      listeners.clear();
    },
  };
}
